import { memo, useMemo } from 'react';
import { getOfficerPluralLabel } from '@/utils/auth';
import { computeTourScheduleState, formatNextOpening } from './tourSchedule';

// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const displayKstTimeFromTourTimeZoneToLocalTimeZone = _wg('displayKstTimeFromTourTimeZoneToLocalTimeZone');

// ── TourCard ─────────────────────────────────────────────────────────

interface TourCardProps {
  tour: any;
  type: 'claimed' | 'unclaimed' | 'active';
  hasClaimedTour: boolean;
  claimingTourId: number | null;
  onView: (tourId: number, tour: any) => void;
  onClaim?: (tour: any) => void;
  onUnclaim?: (tour: any) => void;
}

function TourCard({ tour, type, hasClaimedTour, claimingTourId, onView, onClaim, onUnclaim }: TourCardProps) {
  const isClaiming = claimingTourId != null;
  const isClaimingThis = claimingTourId === tour.id;
  const progressPercent = useMemo(() => {
    if (!tour.totalStops || tour.totalStops === 0) return 0;
    return Math.round((tour.stopsCompleted / tour.totalStops) * 100);
  }, [tour.totalStops, tour.stopsCompleted]);

  function formatTime(timeLocal: string, timeZone: string): string {
    return displayKstTimeFromTourTimeZoneToLocalTimeZone(timeLocal, timeZone);
  }

  return (
    <div className={`tour-card${type === 'claimed' ? ' claimed' : ''}`}>
      <div className="tour-card-header">
        <h3 className="tour-card-title">{tour.title}</h3>
        {tour.clientName && (
          <div className="tour-card-client">{tour.clientName}</div>
        )}
        {type === 'active' && tour.officerNames && tour.officerNames.length > 0 && (
          <div className="tour-card-claimant">
            <i className="fa fa-user"></i> {tour.officerNames.join(', ')}
          </div>
        )}
      </div>
      <div className="tour-card-body">
        <div className="tour-card-info">
          <div className="tour-info-item">
            <span className="tour-info-label">Start Time</span>
            <span className="tour-info-value">{formatTime(tour.startTimeLocal, tour.timeZoneLocal)}</span>
          </div>
          <div className="tour-info-item">
            <span className="tour-info-label">End Time</span>
            <span className="tour-info-value">{formatTime(tour.endTimeLocal, tour.timeZoneLocal)}</span>
          </div>
          <div className="tour-info-item">
            <span className="tour-info-label">Total Stops</span>
            <span className="tour-info-value">{tour.totalStops}</span>
          </div>
          <div className="tour-info-item">
            <span className="tour-info-label">Completed</span>
            <span className="tour-info-value">{tour.stopsCompleted}</span>
          </div>
        </div>
        <div className="tour-card-progress">
          <div className="progress-text">
            <span>Progress</span>
            <span>{progressPercent}%</span>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar" style={{ width: `${progressPercent}%` }}></div>
          </div>
        </div>
      </div>
      <div className="tour-card-actions">
        <button className="btn-view" onClick={(e) => { e.stopPropagation(); onView(tour.id, tour); }}>
          View Tour
        </button>
        {type === 'unclaimed' && !hasClaimedTour && onClaim && (
          <button
            className="btn-claim"
            disabled={isClaiming}
            onClick={(e) => { e.stopPropagation(); onClaim(tour); }}
          >
            {isClaimingThis ? 'Claiming...' : 'Claim'}
          </button>
        )}
        {type === 'active' && !hasClaimedTour && onClaim && (
          <button
            className="btn-claim"
            disabled={isClaiming}
            onClick={(e) => { e.stopPropagation(); onClaim(tour); }}
          >
            {isClaimingThis ? 'Claiming...' : 'Claim'}
          </button>
        )}
        {type === 'claimed' && onUnclaim && (
          <button
            className="btn-unclaim"
            disabled={isClaiming}
            onClick={(e) => { e.stopPropagation(); onUnclaim(tour); }}
          >
            {isClaimingThis ? 'Unclaiming...' : 'Unclaim'}
          </button>
        )}
      </div>
    </div>
  );
}

// ── TourListView ─────────────────────────────────────────────────────

interface TourListViewProps {
  isLoading: boolean;
  activeTab: 'claimed' | 'unclaimed' | 'active';
  claimedTours: any[];
  unclaimedTours: any[];
  activeTours: any[];
  /** Enabled tours' schedules from guardtour/schedule/list (independent of the window-gated lists). */
  tourSchedules: any[];
  hasClaimedTour: boolean;
  claimingTourId: number | null;
  onSetActiveTab: (tab: 'claimed' | 'unclaimed' | 'active') => void;
  onViewTourStops: (tourId: number, tour: any) => void;
  onClaimTour: (tour: any) => void;
  onUnclaimTour: (tour: any) => void;
}

export const TourListView = memo(function TourListView({
  isLoading,
  activeTab,
  claimedTours,
  unclaimedTours,
  activeTours,
  tourSchedules,
  hasClaimedTour,
  claimingTourId,
  onSetActiveTab,
  onViewTourStops,
  onClaimTour,
  onUnclaimTour,
}: TourListViewProps) {
  const officerPluralLabel = useMemo(() => getOfficerPluralLabel(), []);

  // The soonest not-yet-open occurrence across all enabled tours — this is what
  // tells an officer staring at an empty list when a tour will appear.
  const nextOpening = useMemo(() => {
    const now = new Date();
    let soonest: { title: string; start: Date } | null = null;
    for (const schedule of tourSchedules || []) {
      const state = computeTourScheduleState(schedule.availabilities || [], schedule.timeZoneId ?? null, now);
      if (state.status === 'upcoming' && state.start && (!soonest || state.start < soonest.start)) {
        soonest = { title: schedule.title, start: state.start };
      }
    }
    return soonest;
  }, [tourSchedules]);

  const allListsEmpty =
    claimedTours.length === 0 && unclaimedTours.length === 0 && activeTours.length === 0;

  // With a known next opening and nothing open anywhere, the empty state becomes
  // an informative card (mirroring the mockup's dashed info box); otherwise it
  // stays the plain centered text the screen has always used. The "Next" line is
  // gated on allListsEmpty so an empty My Tours tab doesn't point the officer at
  // a future opening while a claimable tour is open right now.
  const emptyState = (message: string) =>
    nextOpening && allListsEmpty ? (
      <div className="empty-state">
        <div className="empty-state-info">
          <div className="empty-state-primary">{message}</div>
          <div className="empty-state-next">
            Next: {nextOpening.title} starts {formatNextOpening(nextOpening.start, new Date(), true)}.
          </div>
        </div>
      </div>
    ) : (
      <div className="empty-state">{message}</div>
    );

  return (
    <>
      <div className="header">
        <h1>Guard Tours</h1>
      </div>

      {/* Tour Tabs */}
      <div className="tour-tabs">
        <button
          className={`tour-tab${activeTab === 'claimed' ? ' active' : ''}`}
          onClick={() => onSetActiveTab('claimed')}
        >
          My Tours
          {claimedTours.length > 0 && <span className="badge">{claimedTours.length}</span>}
        </button>
        <button
          className={`tour-tab${activeTab === 'unclaimed' ? ' active' : ''}`}
          onClick={() => onSetActiveTab('unclaimed')}
        >
          Unclaimed Tours
          {unclaimedTours.length > 0 && <span className="badge">{unclaimedTours.length}</span>}
        </button>
        <button
          className={`tour-tab${activeTab === 'active' ? ' active' : ''}`}
          onClick={() => onSetActiveTab('active')}
        >
          Other {officerPluralLabel}
          {activeTours.length > 0 && <span className="badge">{activeTours.length}</span>}
        </button>
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div>Loading tours...</div>
        </div>
      ) : activeTab === 'claimed' ? (
        claimedTours.length === 0 ? (
          emptyState(
            allListsEmpty
              ? 'No tours are currently in progress.'
              : 'You have no claimed tours. Claim a tour from the Unclaimed Tours tab.'
          )
        ) : (
          <div className="tours-grid">
            {claimedTours.map((tour: any) => (
              <TourCard
                key={tour.id}
                tour={tour}
                type="claimed"
                hasClaimedTour={hasClaimedTour}
                claimingTourId={claimingTourId}
                onView={onViewTourStops}
                onUnclaim={onUnclaimTour}
              />
            ))}
          </div>
        )
      ) : activeTab === 'unclaimed' ? (
        unclaimedTours.length === 0 ? (
          emptyState(
            allListsEmpty ? 'No tours are currently in progress.' : 'No unclaimed tours available.'
          )
        ) : (
          <div className="tours-grid">
            {unclaimedTours.map((tour: any) => (
              <TourCard
                key={tour.id}
                tour={tour}
                type="unclaimed"
                hasClaimedTour={hasClaimedTour}
                claimingTourId={claimingTourId}
                onView={onViewTourStops}
                onClaim={onClaimTour}
              />
            ))}
          </div>
        )
      ) : (
        // active tab
        activeTours.length === 0 ? (
          emptyState(allListsEmpty ? 'No tours are currently in progress.' : 'No active tours.')
        ) : (
          <div className="tours-grid">
            {activeTours.map((tour: any) => (
              <TourCard
                key={tour.id}
                tour={tour}
                type="active"
                hasClaimedTour={hasClaimedTour}
                claimingTourId={claimingTourId}
                onView={onViewTourStops}
                onClaim={onClaimTour}
              />
            ))}
          </div>
        )
      )}
    </>
  );
});
